
import React, { useState } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Facebook, Instagram, Phone, Users, X, Send, ExternalLink, Check, Eye, Contact, Loader } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/use-toast';

const socialPlatforms = [
  {
    id: 'facebook',
    name: 'Facebook',
    icon: <Facebook className="h-5 w-5" />,
    color: 'bg-blue-600',
    textColor: 'text-white',
    authUrl: 'https://www.facebook.com/v11.0/dialog/oauth',
  },
  {
    id: 'instagram',
    name: 'Instagram',
    icon: <Instagram className="h-5 w-5" />,
    color: 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500',
    textColor: 'text-white',
    authUrl: 'https://api.instagram.com/oauth/authorize',
  },
  {
    id: 'twitter',
    name: 'Twitter',
    icon: <X className="h-5 w-5" />,
    color: 'bg-black',
    textColor: 'text-white',
    authUrl: 'https://twitter.com/i/oauth2/authorize',
  },
  {
    id: 'phone',
    name: 'Phone Contacts',
    icon: <Phone className="h-5 w-5" />,
    color: 'bg-green-600',
    textColor: 'text-white',
    authUrl: '',
  },
];

interface InviteModalProps {
  trigger?: React.ReactNode;
}

const InviteModal: React.FC<InviteModalProps> = ({ trigger }) => {
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePhone, setInvitePhone] = useState('');
  const [connectedAccounts, setConnectedAccounts] = useState<string[]>([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [phoneContacts, setPhoneContacts] = useState<{name: string, phone: string}[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState<{name: string, phone: string}[]>([]);
  
  const handleDirectInvite = (type: 'email' | 'phone') => {
    const value = type === 'email' ? inviteEmail : invitePhone;
    
    if (!value) {
      toast({
        title: "Please enter a valid " + type,
        description: `Enter a ${type} to send an invitation.`,
        variant: "destructive"
      });
      return;
    }
    
    toast({
      title: "Invitation sent!",
      description: `Your invitation has been sent to ${value}.`,
      variant: "default"
    });
    
    if (type === 'email') {
      setInviteEmail('');
    } else {
      setInvitePhone('');
    }
  };
  
  const handleConnectAccount = (platform: string, authUrl: string) => {
    if (!authUrl && platform === 'Phone') {
      toast({
        title: "Accessing contacts",
        description: "Requesting permission to access your phone contacts.",
      });
      
      setIsConnecting(true);
      setLoadingContacts(true);
      
      setTimeout(() => {
        const mockContacts = [
          { name: "John Doe", phone: "(555) 123-4567" },
          { name: "Jane Smith", phone: "(555) 987-6543" },
          { name: "Alice Johnson", phone: "(555) 456-7890" },
          { name: "Bob Williams", phone: "(555) 321-6540" },
          { name: "Carol Brown", phone: "(555) 654-0987" },
          { name: "David Miller", phone: "(555) 876-5432" },
          { name: "Eva Davis", phone: "(555) 234-5678" },
          { name: "Frank Wilson", phone: "(555) 890-1234" },
        ];
        
        setPhoneContacts(mockContacts);
        setConnectedAccounts(prev => [...prev, platform]);
        setIsConnecting(false);
        setLoadingContacts(false);
        
        toast({
          title: "Contacts connected!",
          description: `${mockContacts.length} contacts are now available for invitations.`,
          variant: "default"
        });
      }, 1500);
      return;
    }
    
    if (authUrl) {
      setIsConnecting(true);
      
      setTimeout(() => {
        setConnectedAccounts(prev => [...prev, platform]);
        setIsConnecting(false);
        toast({
          title: `${platform} connected!`,
          description: `Your ${platform} account is now connected for invitations.`,
          variant: "default"
        });
      }, 1500);
    }
  };
  
  const invitationMessage = {
    title: "Join me on GivGo!",
    message: "Hey! GivGo is an awesome micro-gifting app that let's friends and family exchange micro-gifts on their special days. It's a fun way to share small tokens of appreciation. Visit the GivGo website, sign up to be notified when GivGo launches. Once they launch, we'll never miss a special day again.",
    cta: "Get GivGo Updates",
    link: "https://www.givgo.io"
  };
  
  const handleContactSelection = (contact: {name: string, phone: string}) => {
    if (selectedContacts.some(c => c.phone === contact.phone)) {
      setSelectedContacts(prev => prev.filter(c => c.phone !== contact.phone));
    } else {
      setSelectedContacts(prev => [...prev, contact]);
    }
  };
  
  const handleSendToSelectedContacts = () => {
    if (selectedContacts.length === 0) {
      toast({
        title: "No contacts selected",
        description: "Please select at least one contact to invite.",
        variant: "destructive"
      });
      return;
    }
    
    toast({
      title: "Sending invitations",
      description: `Sending invitations to ${selectedContacts.length} contacts.`,
    });
    
    setTimeout(() => {
      toast({
        title: "Invitations sent!",
        description: `Successfully invited ${selectedContacts.length} contacts to GivGo.`,
        variant: "default"
      });
      setSelectedContacts([]);
    }, 1500);
  };
  
  const handleSocialInvite = (platform: string) => {
    if (connectedAccounts.includes(platform)) {
      toast({
        title: `Sending ${platform} invitations`,
        description: `Invitations are being sent to your ${platform} contacts.`,
      });
      
      setTimeout(() => {
        toast({
          title: "Invitations sent!",
          description: `Successfully invited your ${platform} contacts to GivGo.`,
          variant: "default"
        });
      }, 1000);
    } else {
      const platformData = socialPlatforms.find(p => p.id === platform.toLowerCase() || p.name === platform);
      if (platformData) {
        handleConnectAccount(platform, platformData.authUrl);
      }
    }
  };
  
  const handleOpenGivGoWebsite = () => {
    window.open(invitationMessage.link, '_blank');
    toast({
      title: "Opening GivGo website",
      description: "Redirecting to the GivGo website for updates.",
    });
  };
  
  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            <span>Invite Friends</span>
          </Button>
        )}
      </DialogTrigger>
      
      {showPreview ? (
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-center">Invitation Preview</DialogTitle>
            <DialogDescription className="text-center">
              This is how your invitation will appear to recipients
            </DialogDescription>
          </DialogHeader>
          
          <div className="p-4 border rounded-md bg-slate-50">
            <div className="flex items-center gap-2 mb-3">
              <img 
                src="/lovable-uploads/00c921fe-bb3a-4eb5-ad50-76a967cd91c7.png" 
                alt="GivGo Logo" 
                className="h-8 w-auto"
              />
              <span className="font-semibold text-givgo-purple">{invitationMessage.title}</span>
            </div>
            <p className="text-sm mb-4">{invitationMessage.message}</p>
            <Button 
              className="w-full bg-givgo-purple hover:bg-givgo-purple/90"
              onClick={handleOpenGivGoWebsite}
            >
              {invitationMessage.cta}
              <ExternalLink className="h-3 w-3 ml-1" />
            </Button>
            <div className="mt-3 text-xs text-center text-muted-foreground">
              Tap the button above or use this link: 
              <div className="mt-1 p-1 bg-white rounded border text-xs break-all flex items-center justify-between">
                <span>{invitationMessage.link}</span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 px-2"
                  onClick={() => {
                    navigator.clipboard.writeText(invitationMessage.link);
                    toast({
                      title: "Link copied!",
                      description: "GivGo website link copied to clipboard",
                    });
                  }}
                >
                  Copy
                </Button>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Close Preview
            </Button>
          </DialogFooter>
        </DialogContent>
      ) : (
        <DialogContent className={connectedAccounts.includes('Phone') && phoneContacts.length > 0 ? "sm:max-w-[600px]" : "sm:max-w-[500px]"}>
          <DialogHeader>
            <DialogTitle className="text-center text-givgo-purple">Invite Friends to GivGo</DialogTitle>
            <DialogDescription className="text-center">
              Share GivGo with your friends and family to start exchanging micro-gifts.
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="direct" className="mt-4">
            <TabsList className="grid grid-cols-2 mb-4">
              <TabsTrigger value="direct">Direct Invite</TabsTrigger>
              <TabsTrigger value="social">Social Media</TabsTrigger>
            </TabsList>
            
            <TabsContent value="direct" className="space-y-4">
              {connectedAccounts.includes('Phone') && phoneContacts.length > 0 ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium">Phone Contacts ({phoneContacts.length})</h3>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setSelectedContacts(phoneContacts)}
                        disabled={selectedContacts.length === phoneContacts.length}
                      >
                        Select All
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setSelectedContacts([])}
                        disabled={selectedContacts.length === 0}
                      >
                        Clear
                      </Button>
                    </div>
                  </div>
                  
                  <div className="border rounded-md overflow-hidden">
                    <div className="max-h-60 overflow-y-auto">
                      {phoneContacts.map((contact, index) => (
                        <div 
                          key={index} 
                          className={`flex items-center justify-between px-3 py-2 border-b last:border-b-0 hover:bg-slate-50 cursor-pointer ${
                            selectedContacts.some(c => c.phone === contact.phone) ? 'bg-givgo-light' : ''
                          }`}
                          onClick={() => handleContactSelection(contact)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-givgo-purple/10 flex items-center justify-center text-givgo-purple">
                              {contact.name.charAt(0)}
                            </div>
                            <div>
                              <div className="font-medium text-sm">{contact.name}</div>
                              <div className="text-xs text-muted-foreground">{contact.phone}</div>
                            </div>
                          </div>
                          <div>
                            {selectedContacts.some(c => c.phone === contact.phone) && (
                              <Check className="h-4 w-4 text-givgo-purple" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="p-3 bg-slate-50 border-t">
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-muted-foreground">
                          {selectedContacts.length} of {phoneContacts.length} selected
                        </div>
                        <Button 
                          size="sm" 
                          className="bg-givgo-purple hover:bg-givgo-purple/90"
                          disabled={selectedContacts.length === 0}
                          onClick={handleSendToSelectedContacts}
                        >
                          <Send className="h-3 w-3 mr-1" />
                          Invite Selected
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium">Email</label>
                    <div className="flex gap-2">
                      <Input
                        id="email"
                        type="email"
                        placeholder="friend@example.com"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                      />
                      <Button 
                        variant="default" 
                        className="bg-givgo-purple hover:bg-givgo-purple/90"
                        onClick={() => handleDirectInvite('email')}
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="phone" className="text-sm font-medium">Phone Number</label>
                    <div className="flex gap-2">
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="(123) 456-7890"
                        value={invitePhone}
                        onChange={(e) => setInvitePhone(e.target.value)}
                      />
                      <Button 
                        variant="default" 
                        className="bg-givgo-purple hover:bg-givgo-purple/90"
                        onClick={() => handleDirectInvite('phone')}
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-givgo-light rounded-md mt-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-givgo-purple text-white p-2 rounded-full">
                        <Phone className="h-4 w-4" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-sm font-medium">Import Phone Contacts</h3>
                        <p className="text-xs text-muted-foreground">Connect to your phone contacts to invite multiple friends at once</p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleConnectAccount('Phone', '')}
                        disabled={isConnecting}
                      >
                        {loadingContacts ? (
                          <Loader className="h-4 w-4 animate-spin" />
                        ) : (
                          <span>Connect</span>
                        )}
                      </Button>
                    </div>
                  </div>
                </>
              )}
              
              <div className="mt-4 p-4 bg-givgo-light rounded-md flex items-center justify-between">
                <div className="text-sm">
                  <span className="font-medium">See what recipients will receive</span>
                </div>
                <Button variant="outline" size="sm" onClick={() => setShowPreview(true)}>
                  <Eye className="h-4 w-4 mr-1" />
                  Preview
                </Button>
              </div>
            </TabsContent>
            
            <TabsContent value="social" className="space-y-4">
              <p className="text-sm text-muted-foreground mb-2">
                Connect your social accounts to invite friends directly from your contacts
              </p>
              
              <div className="grid grid-cols-2 gap-3">
                {socialPlatforms.map((platform) => {
                  const isConnected = connectedAccounts.includes(platform.name);
                  return (
                    <Button
                      key={platform.id}
                      variant="outline"
                      className={`flex items-center justify-start gap-3 h-14 ${platform.color} ${platform.textColor} hover:opacity-90`}
                      onClick={() => handleSocialInvite(platform.name)}
                      disabled={isConnecting}
                    >
                      {platform.icon}
                      <span className="flex-1 text-left">{platform.name}</span>
                      {isConnected ? (
                        <Check className="h-4 w-4 ml-auto" />
                      ) : (
                        <ExternalLink className="h-4 w-4 ml-auto" />
                      )}
                    </Button>
                  );
                })}
                
                <Button
                  variant="outline"
                  className="flex items-center justify-start gap-3 h-14 bg-pink-500 text-white hover:opacity-90"
                  onClick={() => handleSocialInvite('TikTok')}
                  disabled={isConnecting}
                >
                  <span className="text-xl font-bold">TT</span>
                  <span className="flex-1 text-left">TikTok</span>
                  {connectedAccounts.includes('TikTok') ? (
                    <Check className="h-4 w-4 ml-auto" />
                  ) : (
                    <ExternalLink className="h-4 w-4 ml-auto" />
                  )}
                </Button>
                
                <Button
                  variant="outline"
                  className="flex items-center justify-start gap-3 h-14 bg-yellow-400 text-black hover:opacity-90"
                  onClick={() => handleSocialInvite('Snapchat')}
                  disabled={isConnecting}
                >
                  <span className="text-xl">👻</span>
                  <span className="flex-1 text-left">Snapchat</span>
                  {connectedAccounts.includes('Snapchat') ? (
                    <Check className="h-4 w-4 ml-auto" />
                  ) : (
                    <ExternalLink className="h-4 w-4 ml-auto" />
                  )}
                </Button>
              </div>
              
              {connectedAccounts.length > 0 && (
                <div className="mt-4 p-3 bg-givgo-light rounded-md">
                  <h3 className="font-medium text-sm mb-2">Connected Accounts</h3>
                  <div className="flex flex-wrap gap-2">
                    {connectedAccounts.map((account) => (
                      <div key={account} className="px-3 py-1 bg-white rounded-full text-sm flex items-center gap-1">
                        <Check className="h-3 w-3 text-green-500" />
                        {account}
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <Button
                      className="flex-1 bg-givgo-purple hover:bg-givgo-purple/90"
                      onClick={() => {
                        toast({
                          title: "Sending invitations",
                          description: `Inviting friends from ${connectedAccounts.length} connected platforms.`,
                        });
                        setTimeout(() => {
                          toast({
                            title: "Invitations sent!",
                            description: "Successfully invited your contacts to GivGo.",
                          });
                        }, 1500);
                      }}
                    >
                      Invite All Contacts
                    </Button>
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => setShowPreview(true)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
          
          <div className="mt-6 border-t pt-4 text-center text-muted-foreground text-sm">
            Share GivGo and help your friends discover the joy of micro-gifting
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default InviteModal;
